import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import ToolOutlined from '@ant-design/icons/ToolOutlined';
import Card from 'antd/es/card';
import Descriptions from 'antd/es/descriptions';
import Space from 'antd/es/space';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React from 'react';
import { useTheme } from 'styled-components';

import { AVAILABLE_AGENT_TOOLS } from '@mai/types';

import type { AgentFormData } from '../../AgentModals/CreateAgentModal';
import { AGENT_TYPE_PRESETS } from '../presets';
import { ReviewContainer, SummaryCard, InstructionsCard, ConfigSection, CapabilitiesGrid, CapabilityItem } from './AgentReviewForm.styles';

const { Title, Text, Paragraph } = Typography;

interface AgentReviewFormProps {
  formData: AgentFormData;
}

export const AgentReviewForm: React.FC<AgentReviewFormProps> = ({
  formData,
}) => {
  const theme = useTheme();
  const preset =
    formData.agentType !== 'custom'
      ? AGENT_TYPE_PRESETS[formData.agentType]
      : null;

  const getToolNames = () => {
    return formData.tools.map((tool) => {
      const toolInfo =
        AVAILABLE_AGENT_TOOLS[tool.type as keyof typeof AVAILABLE_AGENT_TOOLS];
      return toolInfo?.name || tool.type;
    });
  };

  return (
    <ReviewContainer>
      <Title level={4} style={{ marginBottom: `${theme.margin}px` }}>
        <CheckCircleOutlined
          style={{ color: '#52c41a', marginRight: `${theme.marginXS}px` }}
        />
        Review Your Agent
      </Title>

      <SummaryCard>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text type="secondary">Name</Text>
            <Title level={5} style={{ margin: `${theme.marginXXS}px 0` }}>
              {formData.name}
            </Title>
          </div>

          {formData.description && (
            <div>
              <Text type="secondary">Description</Text>
              <Paragraph style={{ margin: `${theme.marginXXS}px 0` }}>
                {formData.description}
              </Paragraph>
            </div>
          )}

          <div>
            <Text type="secondary">Type</Text>
            <div style={{ marginTop: 4 }}>
              <Tag color="blue" icon={<RobotOutlined />}>
                {preset ? `${preset.icon} ${preset.name}` : '🛠️ Custom Agent'}
              </Tag>
            </div>
          </div>
        </Space>
      </SummaryCard>

      <InstructionsCard title="Instructions">
        <pre>{formData.instructions}</pre>
        {formData.systemPrompt && (
          <ConfigSection>
            <Text type="secondary">System Prompt</Text>
            <pre style={{ marginTop: `${theme.marginXS}px` }}>
              {formData.systemPrompt}
            </pre>
          </ConfigSection>
        )}
      </InstructionsCard>

      <Card title="Capabilities & Configuration">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Tools */}
          <div>
            <Text strong>
              <ToolOutlined /> Available Tools ({formData.tools.length})
            </Text>
            <CapabilitiesGrid>
              {getToolNames().map((toolName, index) => (
                <CapabilityItem key={index}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text>{toolName}</Text>
                </CapabilityItem>
              ))}
              {formData.tools.length === 0 && (
                <Text type="secondary">No tools selected</Text>
              )}
            </CapabilitiesGrid>
          </div>

          {/* Configuration Summary */}
          <Descriptions column={2} size="small">
            <Descriptions.Item label="LLM Provider">
              <Tag>
                {formData.llmConfig.thought_llm?.provider || 'vertexai'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Model Size">
              <Tag>{formData.llmConfig.thought_llm?.model_tier || 'large'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Max Actions">
              {formData.executionConfig.max_actions}
            </Descriptions.Item>
            <Descriptions.Item label="Planning Interval">
              Every {formData.executionConfig.planning_interval} actions
            </Descriptions.Item>
            <Descriptions.Item label="Timeout">
              <Space>
                <ClockCircleOutlined />
                {formData.executionConfig.timeout_minutes} minutes
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Card>
    </ReviewContainer>
  );
};
